<!--
  Pick the job site a form belongs to. Lists active sites (and the current
  one even if finished), with "+ New site…" opening an inline name/city
  mini-form so a site that is not on the list yet never blocks field work.
  On choose, hands back the site so the form can write both its id and its
  name (into the typed job column the printed sheet uses).
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { notifyError } from '../../lib/store';
  import type { SiteSummary } from '../../lib/types';

  interface Props {
    value: number | null;
    onchoose: (site: SiteSummary | null) => void;
  }
  let { value, onchoose }: Props = $props();

  let sites = $state<SiteSummary[]>([]);
  let adding = $state(false);
  let newName = $state('');
  let newCity = $state('');
  let addError = $state<string | null>(null);

  $effect(() => {
    api.listSites().then((r) => { sites = r.sites; }).catch(() => { /* the typed job text still saves */ });
  });

  const options = $derived(sites.filter((s) => s.status === 'active' || s.id === value));

  function choose(select: HTMLSelectElement) {
    const raw = select.value;
    if (raw === '__new') {
      adding = true;
      // The <select>'s DOM value doesn't revert on its own: the `value`
      // prop driving it hasn't changed, so Svelte has nothing to re-push.
      // Snap it back to the current selection now, or "+ New site…" stays
      // shown even after Cancel.
      select.value = value === null ? '' : String(value);
      return;
    }
    const id = raw === '' ? null : Number(raw);
    onchoose(sites.find((s) => s.id === id) ?? null);
  }

  async function addSite() {
    addError = null;
    try {
      const created = await api.createSite({ name: newName, city: newCity });
      const summary: SiteSummary = { ...created, jha_count: 0, field_test_count: 0, equipment_count: 0, daily_report_count: 0, contract_report_count: 0, last_activity: null };
      sites = [...sites, summary];
      adding = false;
      newName = '';
      newCity = '';
      onchoose(summary);
    } catch (cause) {
      addError = isApiError(cause) ? cause.message : 'Could not add the site.';
      notifyError(cause, 'Could not add the site');
    }
  }
</script>

<label class="field">
  <span class="label">Job site</span>
  <select value={value === null ? '' : String(value)} onchange={(e) => choose(e.currentTarget as HTMLSelectElement)}>
    <option value="">— choose a site —</option>
    {#each options as s (s.id)}
      <option value={String(s.id)}>{s.name}{s.city ? ` — ${s.city}` : ''}{s.status === 'finished' ? ' (finished)' : ''}</option>
    {/each}
    <option value="__new">+ New site…</option>
  </select>
</label>
{#if adding}
  <div class="newsite">
    <label class="field"><span class="label">Site name</span><input type="text" bind:value={newName} /></label>
    <label class="field"><span class="label">City</span><input type="text" bind:value={newCity} /></label>
    {#if addError}<p class="field__flag">{addError}</p>{/if}
    <div class="btnrow">
      <button class="btn btn--primary btn--sm" type="button" onclick={() => void addSite()}>Add site</button>
      <button class="btn btn--sm" type="button" onclick={() => (adding = false)}>Cancel</button>
    </div>
  </div>
{/if}

<style>
  .newsite { border: 1px solid var(--line); border-radius: var(--radius); padding: 0.6rem; margin: 0.4rem 0 0.8rem; background: var(--bg); }
</style>
